/* eslint-disable no-restricted-globals */
import moment from "moment";
import React from "react";
import { FaTelegramPlane, FaDownload } from "react-icons/fa";
import addPrecision from "../../common/addPrecision";
import AppButton from "../../common/AppButton";
import Table from "../../common/Table";
import api from "../../config/api";
import { getOrganizations } from "../../stores/OrganizationStore";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getLocations } from "../../stores/LocationStore";
import { useState } from "react";
import { useEffect } from "react";
import ContractInvoicePdfTemplate from "./ContractInvoicePdfTemplate";
import { usePDF } from "react-to-pdf";
import { toast } from "react-toastify";
import html2pdf from "html2pdf.js";
import JSZip from "jszip";
import { saveAs } from "file-saver";

const header = [
  { name: "contractId", title: "Contract ID", filter: "Search" },
  { name: "location", title: "Client Address", trunecate: true },
  { name: "month", title: "Month" },
  { name: "amount", title: "Price" },
];

const InvoiceToBeSent = ({ list }) => {
  const queryClient = useQueryClient();
  const [selectedIds, setSelectedIds] = useState([]);
  const [invoice, setInvoice] = useState({});
  const [contract, setContract] = useState({});
  const [fileName, setFileName] = useState("");
  const [download, setDownload] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [currentInvoiceData, setCurrentInvoiceData] = useState(null);
  const [errors, setErrors] = useState([]);
  const invoiceRef = React.useRef(null);

  const { data: organizations } = useQuery({
    queryFn: () => getOrganizations(),
    queryKey: ["organizations"],
  });

  const { data: locations } = useQuery({
    queryFn: () => getLocations(),
    queryKey: ["locations"],
  });

  const handleSend = async (inv) => {
    if (confirm("Generate invoice number and marked as sent") !== true)
      return false;
    setDownload(true);
    const res = await api.post("contractInvoice", inv);

    if (!res.ok) return toast.error(res.originalError.message);
    setInvoice({ ...inv, invoiceID: res.data?.invoiceID });

    setFileName(
      `INV - ${inv.contract?.organizationName} - ${res.data?.invoiceID}.pdf`
    );
  };

  const generatePDFBlob = async (element, filename) => {
    const opt = {
      margin: 0,
      filename: filename,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: {
        scale: 2,
        useCORS: true,
        letterRendering: true,
        logging: false,
      },
      jsPDF: {
        unit: "in",
        format: "letter",
        orientation: "portrait",
      },
    };

    try {
      const pdfBlob = await html2pdf().from(element).set(opt).outputPdf("blob");
      return pdfBlob;
    } catch (error) {
      console.error("Error generating PDF:", error);
      throw error;
    }
  };

  const handleDownload = async () => {
    if (!selectedIds.length) {
      toast.error("Please select at least one invoice to download");
      return;
    }

    if (
      confirm(
        `Generate ${selectedIds.length} invoice number(s) and mark as sent?`
      ) !== true
    ) {
      return false;
    }

    setIsGenerating(true);
    setProgress({ current: 0, total: selectedIds.length });
    setErrors([]);

    const ModifiedList = list
      ?.map((dt) => {
        return {
          ...dt,
          client: organizations?.find(
            (item) => item._id === dt.contract?.organization
          )?.name,
          location: dt.contract.location.map(
            (i) => locations?.find((item) => item._id === i)?.title
          ),
          contractId: dt.contract.contractId,
          amount: `$ ${addPrecision(dt.price)}`,
          month: moment(dt.startDate).format("MMMM"),
          id: `${dt.contract.contractId}-${moment(dt.startDate).format(
            "MMMM"
          )}`,
        };
      })
      .filter((modified) => selectedIds.includes(modified.id));

    const zip = new JSZip();
    const failedInvoices = [];

    for (let i = 0; i < ModifiedList.length; i++) {
      try {
        const inv = ModifiedList[i];
        setProgress({ current: i + 1, total: ModifiedList.length });

        // Generate invoice number via API
        const res = await api.post("contractInvoice", inv);

        if (!res.ok) {
          throw new Error(
            res.originalError?.message || "Failed to create invoice"
          );
        }

        const invoiceWithID = { ...inv, invoiceID: res.data?.invoiceID };

        // Prepare contract data
        const contractData = {
          ...inv.contract,
          organization: organizations?.find(
            (item) => item._id === inv.contract?.organization
          ),
          location: inv.location?.map((i) => i),
        };

        // Update current invoice for rendering
        setCurrentInvoiceData({
          invoice: invoiceWithID,
          contract: contractData,
        });

        // Wait for DOM to update and render
        await new Promise((resolve) => setTimeout(resolve, 500));

        if (!invoiceRef.current) {
          throw new Error("Invoice template ref not found");
        }

        // Generate PDF blob
        const filename = `INV - ${
          inv.contract?.organizationName || "Unknown"
        } - ${res.data?.invoiceID}.pdf`;

        const pdfBlob = await generatePDFBlob(invoiceRef.current, filename);

        // Add to zip
        zip.file(filename, pdfBlob);
      } catch (error) {
        console.error(`Failed to generate invoice ${i + 1}:`, error);
        failedInvoices.push({
          index: i + 1,
          invoiceID: ModifiedList[i]?.contractId || "Unknown",
          organizationName:
            ModifiedList[i]?.contract?.organizationName || "Unknown",
          error: error.message,
        });
      }
    }

    try {
      if (Object.keys(zip.files).length === 0) {
        toast.error("No invoices were successfully generated.");
        setIsGenerating(false);
        setCurrentInvoiceData(null);
        setProgress({ current: 0, total: 0 });
        return;
      }

      const zipBlob = await zip.generateAsync({
        type: "blob",
        compression: "DEFLATE",
        compressionOptions: { level: 6 },
      });

      const zipFilename = `Contract-Invoices-${moment().format(
        "YYYY-MM-DD-HHmmss"
      )}.zip`;
      saveAs(zipBlob, zipFilename);

      if (failedInvoices.length > 0) {
        setErrors(failedInvoices);
        const successCount = ModifiedList.length - failedInvoices.length;
        toast.warning(
          `Successfully generated ${successCount} of ${ModifiedList.length} invoices. ${failedInvoices.length} failed.`
        );
      } else {
        toast.success(
          `Successfully generated all ${ModifiedList.length} invoices!`
        );
        setSelectedIds([]); // Clear selection after successful download
      }

      queryClient.invalidateQueries(["contractInvoice"]);
    } catch (error) {
      console.error("Failed to create zip file:", error);
      toast.error("Failed to create zip file. Please try again.");
    }

    setIsGenerating(false);
    setCurrentInvoiceData(null);
    setProgress({ current: 0, total: 0 });
  };

  useEffect(() => {
    if (organizations && Object.keys(invoice)?.length) {
      const contract = {
        ...invoice.contract,
        organization: organizations?.find(
          (item) => item._id === invoice.contract?.organization
        ),
        location: invoice.location?.map((i) => i),
      };

      setContract(contract);
    }
  }, [invoice]);

  useEffect(() => {
    if (
      Object.keys(invoice)?.length &&
      Object.keys(contract)?.length &&
      contract.invoice_text
    ) {
      if (download) toPDF();
      setDownload(false);

      toast.success("Invoice has been successfully downloaded");
      queryClient.invalidateQueries(["contractInvoice"]);
      setContract({});
      setInvoice({});
    }
  }, [contract]);

  const { toPDF, targetRef } = usePDF({
    filename: fileName,
    page: {
      format: "letter",
    },
  });

  const tableData =
    list?.map((dt) => {
      return {
        ...dt,
        client: organizations?.find(
          (item) => item._id === dt.contract?.organization
        )?.name,
        location: dt.contract.location.map(
          (i) => locations?.find((item) => item._id === i)?.title
        ),
        contractId: dt.contract.contractId,
        amount: `$ ${dt.total}`,
        month: moment(dt.startDate).format("MMMM"),
        id: `${dt.contract.contractId}-${moment(dt.startDate).format("MMMM")}`,
      };
    }) || [];

  return (
    <React.Fragment>
      {/* Loading Overlay */}
      {isGenerating && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.85)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999,
            flexDirection: "column",
            gap: 20,
          }}
        >
          <div
            style={{
              width: 60,
              height: 60,
              border: "5px solid #f3f3f3",
              borderTop: "5px solid #3B7FF6",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
            }}
          />
          <p style={{ color: "white", fontSize: "1.5rem", fontWeight: "bold" }}>
            Generating invoice {progress.current} of {progress.total}...
          </p>
          <div
            style={{
              width: "400px",
              height: "20px",
              backgroundColor: "#333",
              borderRadius: "10px",
              overflow: "hidden",
              border: "2px solid #555",
            }}
          >
            <div
              style={{
                width: `${(progress.current / progress.total) * 100}%`,
                height: "100%",
                backgroundColor: "#3B7FF6",
                transition: "width 0.3s ease",
              }}
            />
          </div>
          <p style={{ color: "#ccc", fontSize: "1rem" }}>
            Please do not close or navigate away from this page
          </p>
        </div>
      )}

      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>

      {/* Error Display */}
      {errors.length > 0 && (
        <div
          style={{
            marginBottom: 15,
            padding: 15,
            backgroundColor: "#fff3cd",
            border: "1px solid #ffc107",
            borderRadius: 5,
            maxHeight: "200px",
            overflowY: "auto",
          }}
        >
          <p style={{ fontWeight: "bold", marginBottom: 10, color: "#856404" }}>
            ⚠️ Failed to generate {errors.length} invoice(s):
          </p>
          {errors.map((err, idx) => (
            <div
              key={idx}
              style={{ fontSize: "0.9rem", margin: "5px 0", color: "#856404" }}
            >
              <strong>• Invoice #{err.index}:</strong> {err.organizationName} -{" "}
              {err.invoiceID}
              <br />
              <span style={{ fontSize: "0.85rem", paddingLeft: "15px" }}>
                Error: {err.error}
              </span>
            </div>
          ))}
        </div>
      )}

      <div
        style={{
          marginBottom: 15,
          display: "flex",
          gap: 10,
          alignItems: "center",
        }}
      >
        <button
          className="btn btn-primary"
          onClick={handleDownload}
          disabled={isGenerating || !selectedIds.length}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "8px 16px",
            cursor:
              isGenerating || !selectedIds.length ? "not-allowed" : "pointer",
            opacity: isGenerating || !selectedIds.length ? 0.6 : 1,
          }}
        >
          <FaDownload />
          Download Selected ({selectedIds.length})
        </button>
        <button
          className="btn btn-outline-secondary"
          onClick={() => setSelectedIds([])}
          disabled={isGenerating || !selectedIds.length}
          style={{
            cursor:
              isGenerating || !selectedIds.length ? "not-allowed" : "pointer",
            opacity: isGenerating || !selectedIds.length ? 0.6 : 1,
          }}
        >
          Clear Selection
        </button>
      </div>

      <Table
        header={header}
        selectIdentifier="id"
        selectedRows={selectedIds}
        setSelectedRows={setSelectedIds}
        data={tableData}
        SideButtons={({ inv }) => {
          return (
            <div className="side-button">
              <AppButton
                tooltip="Generate Invoice"
                className="text-primary"
                leftIcon={<FaTelegramPlane />}
                onClick={() => handleSend(inv)}
                disabled={isGenerating}
              />
            </div>
          );
        }}
      />

      <div
        ref={targetRef}
        style={{
          position: "absolute",
          left: "-9999px",
          top: 0,
        }}
      >
        <ContractInvoicePdfTemplate invoice={invoice} contract={contract} />
      </div>

      {/* Hidden div for bulk PDF generation */}
      <div
        style={{
          position: "absolute",
          left: "-9999px",
          top: 0,
          width: "8.5in",
        }}
      >
        <div ref={invoiceRef}>
          {currentInvoiceData && (
            <ContractInvoicePdfTemplate
              invoice={currentInvoiceData.invoice}
              contract={currentInvoiceData.contract}
            />
          )}
        </div>
      </div>
    </React.Fragment>
  );
};

export default InvoiceToBeSent;
